// src/MudarChaleModal.js

import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, Box, Typography, Divider, FormControl,
  InputLabel, Select, MenuItem, TextField, CircularProgress
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

const MudarChaleModal = ({ open, reserva, chales, onClose, onConfirm }) => {
  const [chalesDisponiveis, setChalesDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chaleDestino, setChaleDestino] = useState('');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    const verificarDisponibilidade = async () => {
      if (!open || !reserva || chales.length === 0) return;
      
      setLoading(true);
      
      // --- LÓGICA DE DATAS ATUALIZADA ---
      const hoje = dayjs().format('YYYY-MM-DD');
      const estaHospedado = reserva.statusReserva === 'Hospedado';
      // Se já fez check-in, o período a verificar é de hoje até o checkout.
      // Se não fez check-in, o período a verificar é o da reserva completa.
      const dataInicioVerificacao = estaHospedado ? hoje : reserva.dataCheckin;
      const dataFimVerificacao = reserva.dataCheckout;

      const chalesParaVerificar = chales.filter(c => c.id !== reserva.chaletId);
      const disponiveis = [];

      for (const chale of chalesParaVerificar) {
        // Verificar conflitos de reserva
        const reservasRef = collection(db, 'reservas');
        const qReservas = query(
          reservasRef,
          where('chaletId', '==', chale.id),
          where('dataCheckin', '<', dataFimVerificacao),
          where('dataCheckout', '>', dataInicioVerificacao)
        );
        const reservasSnap = await getDocs(qReservas);

        // Verificar conflitos de manutenção
        const manutencoesRef = collection(db, 'manutencoes');
        const qManutencoes = query(
            manutencoesRef,
            where('chaleId', '==', chale.id),
            where('dataInicio', '<=', dataFimVerificacao),
            where('dataFim', '>=', dataInicioVerificacao)
        );
        const manutencoesSnap = await getDocs(qManutencoes);
        
        if (reservasSnap.empty && manutencoesSnap.empty) {
          disponiveis.push(chale);
        }
      }

      setChalesDisponiveis(disponiveis);
      setLoading(false);
    };

    verificarDisponibilidade();
  }, [open, reserva, chales]);

  const handleConfirmar = () => {
    if (!chaleDestino) {
      toast.error('Por favor, selecione um chalé de destino.');
      return;
    }
    if (!motivo.trim()) {
      toast.error('Por favor, informe o motivo da mudança.');
      return;
    }
    const chaleSelecionado = chales.find(c => c.id === chaleDestino);
    onConfirm(chaleSelecionado, motivo);
  };
  
  const periodoRestante = reserva.statusReserva === 'Hospedado' 
    ? `Hoje até ${dayjs(reserva.dataCheckout).format('DD/MM/YYYY')}`
    : `${dayjs(reserva.dataCheckin).format('DD/MM/YYYY')} a ${dayjs(reserva.dataCheckout).format('DD/MM/YYYY')}`;


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHorizIcon />
        Mudar Hóspede de Chalé
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Verificando chalés disponíveis...</Typography>
            </Box>
        ) : (
            <Box>
                <Typography gutterBottom>
                    Hóspede: <strong>{reserva.hospedeNome}</strong>
                </Typography>
                <Typography gutterBottom>
                    Chalé Atual: <strong>{reserva.chaleNome}</strong>
                </Typography>
                <Typography gutterBottom>
                    Período: <strong>{periodoRestante}</strong>
                </Typography>
                <Divider sx={{ my: 2 }} />
                <FormControl fullWidth sx={{ my: 2 }}>
                    <InputLabel id="select-chale-destino-label">Selecione o Chalé de Destino</InputLabel>
                    <Select
                        labelId="select-chale-destino-label"
                        value={chaleDestino}
                        label="Selecione o Chalé de Destino"
                        onChange={(e) => setChaleDestino(e.target.value)}
                    >
                        {chalesDisponiveis.length > 0 ? (
                            chalesDisponiveis.map(chale => (
                                <MenuItem key={chale.id} value={chale.id}>{chale.nome}</MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>Nenhum chalé disponível para o período</MenuItem>
                        )}
                    </Select>
                </FormControl>
                <TextField
                    label="Motivo da Mudança"
                    fullWidth
                    multiline
                    rows={3}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                />
            </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} variant="outlined">Cancelar</Button>
        <Button onClick={handleConfirmar} variant="contained" disabled={loading || chalesDisponiveis.length === 0}>
          Confirmar Mudança
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MudarChaleModal;

